import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Observable, catchError, filter, of, shareReplay, switchMap, take, tap } from 'rxjs';

import { BusinessAreaEnum } from 'app/core/enums/generated/BusinessAreaEnum';
import { PermissionRole } from 'app/core/enums/generated/PermissionRole';
import { SecurityService } from 'app/core/security/security.service';
import { BcdDashboardVm } from 'app/shared/generated/InsulinProgram/Models/BcdDashboardVm';
import { InsulinHeaderFacilityService } from './insulin-header-facility.service';

/**
 * The single source for the Insulin dashboard figures, shared by the Insulin Program dashboard page
 * and by the two widgets on HOMER's own dashboard.
 *
 * One service rather than one per consumer because the two widgets render side by side on the same
 * screen and ask the same question. Without the in-flight join below they would issue two identical
 * requests on every load, and - worse - could disagree, because each response is a separate snapshot
 * of a queue other people are working. Joining them means both widgets always draw the same moment.
 *
 * Deliberately not a cache. A joined request is dropped as soon as it answers, so the next refresh
 * goes back to the server. The acceptance criteria require that losing access clears what is on
 * screen rather than leaving the last good answer behind, and a cache keyed on anything the client
 * knows cannot tell "the same user" from "the same user whose access was just revoked".
 */
@Injectable({ providedIn: 'root' })
export class InsulinDashboardService {

    private readonly http = inject(HttpClient);
    private readonly sec = inject(SecurityService);
    private readonly header = inject(InsulinHeaderFacilityService);

    /** Emits once, as soon as the header facility is known. */
    private readonly headerReady$ = toObservable(this.header.ready).pipe(filter(Boolean), take(1));

    private readonly url = 'api/InsulinProgram/InsulinDashboard/Dashboard';

    // #region State

    /**
     * The last answer from the server, or null when there is nothing to show.
     *
     * Null rather than an empty payload while loading and after a failure, so a template can tell
     * "we do not know yet" from "we asked and the answer was nothing". They are different sentences
     * to a reader and the acceptance criteria ask for both.
     */
    readonly dashboard = signal<BcdDashboardVm | null>(null);

    readonly isLoading = signal<boolean>(false);

    /** Set when the request failed. The widgets say so rather than showing zeros, which would lie. */
    readonly hasError = signal<boolean>(false);

    /**
     * What the widgets say when the server answers "not enabled".
     *
     * With a header facility selected the reason is known: that facility is one of the reader's (it
     * would not be sent otherwise) and they hold the module (or nothing would be asked), so the only
     * thing left is that nobody has switched the widgets on for it. Saying "not available for your
     * account" there sent people looking at their access. Without one, any of the reasons may apply,
     * so the message stays general.
     */
    readonly unavailableMessage = computed(() => {
        const name = this.header.facilityName();
        return name
            ? `The Insulin widgets are turned off for ${name}. A System Admin can turn them on under Insulin Distribution Program Admin, Dashboard Widgets.`
            : 'The Insulin widgets are not available for your account.';
    });

    // #endregion

    // #region Private

    /** The request currently in flight, so simultaneous callers share one round trip. */
    private inFlight: Observable<BcdDashboardVm | null> | null = null;

    /**
     * The facility the in-flight request is for: a number, null for all of them, or undefined while
     * the header facility is not yet known. A caller asking for a different one starts its own.
     */
    private inFlightFacility: number | null | undefined = undefined;

    // #endregion

    /**
     * Fetches the dashboard, joining a request already in flight.
     *
     * Asks for the facility selected in HOMER's header when it is one of the reader's Insulin
     * facilities, and for every facility they are associated with otherwise
     * (InsulinHeaderFacilityService). The widgets' links into the queue name the same
     * facility, so a count here and the list it opens cannot disagree. The server checks a named
     * facility against the caller's own associations; nothing the client sends can widen them.
     *
     * Waits for the header facility to be known before asking, so the first answer is already for
     * the right facility rather than all of them and then one.
     */
    refresh(): Observable<BcdDashboardVm | null> {
        // Without the module, do not ask. The endpoint is guarded by ModuleAccessFilter and answers
        // 403, and the http interceptor answers a refused GET by swapping the whole page for Access
        // Denied - here, the dashboard the widgets sit on, for a user whose access had been removed.
        // Every other HOMER widget that needs a module checks the same way before calling;
        // sys-inv-cycle-count-widget is the pattern this follows.
        //
        // A guard against a state that should not arise rather than a permission check: revoking
        // module access already removes the user's widgets, and the widget list no longer offers
        // them. Nothing here grants anything - the server decides, and this only decides whether to
        // bother asking.
        if (!this.hasModuleAccess()) {
            this.dashboard.set(null);
            this.hasError.set(false);
            this.isLoading.set(false);
            return of(null);
        }

        const facility = this.header.ready() ? this.header.facilityId() : undefined;
        if (this.inFlight && this.inFlightFacility === facility) { return this.inFlight; }

        // Clear before asking, not after answering. Between a revoked permission and the response
        // that reflects it, the old rows would otherwise stay on screen - which is exactly the stale
        // protected data the acceptance criteria rule out.
        this.dashboard.set(null);
        this.hasError.set(false);
        this.isLoading.set(true);

        const request = this.headerReady$.pipe(
            switchMap(() => {
                const facilityId = this.header.facilityId();
                const params = facilityId == null
                    ? undefined
                    : new HttpParams().set('facilityId', facilityId);
                return this.http.get<BcdDashboardVm>(this.url, { params });
            })
            , tap(vm => {
                // A newer request for another facility has taken over; its answer is the one to show.
                if (this.inFlight !== request) { return; }
                this.dashboard.set(vm ?? null);
                this.isLoading.set(false);
                this.clearInFlight();
            })
            , catchError(() => {
                if (this.inFlight !== request) { return of(null); }
                // Surfaced, not swallowed: the widget says the figures could not be loaded. Showing
                // zeros here would read as "no work today" to the person who has to act on it.
                this.hasError.set(true);
                this.dashboard.set(null);
                this.isLoading.set(false);
                this.clearInFlight();
                return of(null);
            })
            // No refCount, so a consumer that is destroyed mid-request does not cancel the request
            // the other widget is still waiting on.
            , shareReplay({ bufferSize: 1, refCount: false })
        );
        this.inFlight = request;
        this.inFlightFacility = facility;

        return this.inFlight;
    }

    /** Whether the signed-in user holds the Insulin Program module at the minimum role. */
    private hasModuleAccess(): boolean {
        return this.sec.hasModuleAccess(
            [ BusinessAreaEnum.InsulinProgram ]
            , SecurityService.setMinRole(PermissionRole.User)
        );
    }

    private clearInFlight(): void {
        this.inFlight = null;
        this.inFlightFacility = undefined;
    }
}
